import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiX, FiCheck, FiArrowRight } from 'react-icons/fi';
import { isValidEmail, offerState } from '../../lib/aiKit';
import { businessLogic } from '../../lib/constants';
import { trackConversion } from '../../lib/analytics';

/**
 * El único formulario de la landing: todos los CTA del kit abren este.
 *
 * Decisiones que vienen del brief y que conviene no "simplificar" después:
 *   - UN SOLO CAMPO: el email. Ni nombre ni teléfono en esta primera conversión.
 *   - DOS casillas y no una, porque son dos cosas distintas: aceptar los
 *     Términos es OBLIGATORIO (y es lo que se manda como `privacy_policy`),
 *     y la newsletter es OPCIONAL y desmarcada, y es lo único que cuenta como
 *     consentimiento comercial. Aceptar los Términos NO lo sustituye
 *     (art. 21 LSSI; RGPD art. 7).
 *   - Todo lo legal apunta a los TÉRMINOS, no a la política de privacidad: esa
 *     está publicada a nombre de otra sociedad y no está autorizada. El párrafo
 *     largo de protección de datos se retiró por decisión del dueño: queda el
 *     enlace a los Términos en la casilla obligatoria, y el detalle se da en el
 *     correo de entrega.
 *   - El cierre es visible e inmediato, sin textos culpabilizadores.
 *   - La confirmación vive AQUÍ, no en otra página, y solo aparece cuando el
 *     servidor ha aceptado la solicitud.
 *
 * Accesibilidad: role="dialog" + aria-modal, foco al campo, Escape cierra, Tab
 * circula dentro del diálogo y al cerrar el foco vuelve al botón que lo abrió.
 *
 * `variant="newsletter"` (popup de la home): el mismo formulario, pero es un alta
 * en la NEWSLETTER y no hay kit. Cambian los textos, desaparece la casilla
 * opcional de newsletter (suscribirse ES eso: el botón es el acto de
 * consentimiento, con la frase que lo explica junto al botón, y se envía
 * `enable_newsletter: true`) y la confirmación no habla de kit ni de oferta.
 * La casilla de los Términos sigue siendo obligatoria.
 */

const COPY = {
  kit: {
    title: 'aikit_form_title',
    desc: 'aikit_form_desc',
    submit: 'aikit_form_submit',
    termsError: 'aikit_form_terms_error',
    error: 'aikit_form_error',
    support: 'aikit_form_support',
    ok: 'aikit_form_ok',
    okNoEmail: 'aikit_form_ok_noemail',
    doneText: 'aikit_done_text',
    doneTextNoEmail: 'aikit_done_text_noemail',
    okOpen: 'aikit_form_ok_open',
  },
  // /resources/ai-commercial-system: el mismo formulario con el recurso comercial.
  commercial: {
    title: 'aics_form_title',
    desc: 'aics_form_desc',
    submit: 'aics_form_submit',
    termsError: 'aics_form_terms_error',
    error: 'aikit_form_error',
    support: 'aikit_form_support',
    ok: 'aics_form_ok',
    okNoEmail: 'aics_form_ok_noemail',
    doneText: 'aikit_done_text',
    doneTextNoEmail: 'aics_done_text_noemail',
    okOpen: 'aics_form_ok_open',
  },
  newsletter: {
    title: 'newsletter_popup_title',
    desc: 'newsletter_popup_desc',
    submit: 'newsletter_popup_submit',
    termsError: 'newsletter_popup_terms_error',
    error: 'newsletter_popup_error',
    support: 'newsletter_popup_consent',
  },
};

const KitRequestModal = ({ open, onClose, onSubmit, onSeeOffer, returnFocusTo, variant = 'kit' }) => {
  const { t } = useTranslation();
  const isNewsletter = variant === 'newsletter';
  const copy = COPY[variant] || COPY.kit;
  const dialogRef = useRef(null);
  const emailRef = useRef(null);
  const termsRef = useRef(null);
  const sendingRef = useRef(false);

  const [email, setEmail] = useState('');
  const [terms, setTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [status, setStatus] = useState('idle');   // idle | sending | error | done
  const [fieldError, setFieldError] = useState('');
  const [termsError, setTermsError] = useState('');
  const [result, setResult] = useState(null);

  const sending = status === 'sending';
  const done = status === 'done';
  // La oferta del webinar es del kit: la landing comercial no la enseña nunca.
  const offerLive = variant === 'kit' && offerState() === 'live';

  // Al reabrir se conserva el email escrito (cerrar sin querer no debería costar
  // volver a teclearlo) pero NO el error ni un envío anterior.
  useEffect(() => {
    if (!open) return;
    setFieldError('');
    setTermsError('');
    if (status !== 'done') setStatus('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { sendingRef.current = sending; }, [sending]);

  // Foco inicial + bloqueo del scroll de fondo mientras está abierto.
  useEffect(() => {
    if (!open) return undefined;

    // EL BLOQUEO VA EN <html>, NO SOLO EN <body>. La web pone `html {
    // overflow-x: hidden }` (_common.scss), y eso convierte a <html> en el
    // elemento que desplaza la página.
    const root = document.documentElement;
    // Donde estaba el foco al abrir: si no hay botón de origen (apertura
    // automática), se vuelve ahí y no al <body>.
    const previousFocus = document.activeElement;
    const previousBody = document.body.style.overflow;
    const previousRoot = root.style.overflow;
    const previousPad = root.style.paddingRight;
    // Compensa el ancho de la barra de desplazamiento: al ocultarla, en
    // escritorio la página se desplazaría ~15 px a la derecha de golpe.
    const scrollbar = window.innerWidth - root.clientWidth;
    document.body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
    // Oculta los flotantes del sitio mientras el formulario está abierto: el
    // lanzador del chatbot va con z-index máximo y, en la home, se plantaba
    // ENCIMA del formulario tapando el texto de la casilla. Ver _ai-kit.scss.
    document.body.classList.add('aik-modal-open');

    const timer = setTimeout(() => {
      if (emailRef.current) emailRef.current.focus();
    }, 40);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = previousBody;
      root.style.overflow = previousRoot;
      root.style.paddingRight = previousPad;
      document.body.classList.remove('aik-modal-open');
      // El valor que queremos es el de AHORA, al cerrar: el botón que abrió el
      // diálogo. Puede haber desaparecido (la apertura automática no tiene
      // botón de origen): entonces, lo que tenía el foco al abrir (sin mover la
      // página) y, si tampoco, el contenido principal. Sin red, el foco se iría
      // al <body> y el teclado perdería su sitio.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const trigger = returnFocusTo && returnFocusTo.current;
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      else if (previousFocus && previousFocus !== document.body && previousFocus.isConnected
        && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      } else {
        const main = document.getElementById('contenido');
        if (main) main.focus();
      }
    };
  }, [open, returnFocusTo]);

  // Escape cierra; Tab no se escapa del diálogo.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        // Con el envío en vuelo no se cierra: la petición seguiría viva.
        if (!sendingRef.current) onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      // Se filtra por visibilidad real: un enlace dentro de un bloque oculto
      // aparece en el selector pero no es enfocable, y con la lista mal
      // calculada el tabulador se escapa del diálogo.
      const focusables = Array.prototype.filter.call(
        dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),'
          + ' textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        ),
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFieldError('');
    setTermsError('');

    if (!isValidEmail(email)) {
      setFieldError(t('aikit_form_error_email'));
      if (emailRef.current) emailRef.current.focus();
      return;
    }
    // Sin aceptar los Términos no se envía nada: el `privacy_policy` que llega
    // al CRM tiene que corresponder a un acto real del visitante.
    if (!terms) {
      setTermsError(t(copy.termsError));
      if (termsRef.current) termsRef.current.focus();
      return;
    }

    setStatus('sending');
    // Newsletter: el alta ES el consentimiento (siempre true). Kit: su casilla.
    const res = await onSubmit({
      email: email.trim(),
      terms,
      newsletter: isNewsletter || newsletter,
    });

    // La confirmación SOLO se enseña si el servidor ha aceptado la solicitud.
    // Nada de temporizadores ni de "parece que ha ido bien".
    if (res && res.ok) {
      setResult(res);
      setStatus('done');
      return;
    }
    if (res && res.error === 'invalid') {
      setStatus('idle');
      setFieldError(t('aikit_form_error_email'));
      if (emailRef.current) emailRef.current.focus();
      return;
    }
    setStatus('error');
  };

  // Si el kit no ha salido por correo pero el core sí ha devuelto el contenido,
  // se abre en una pestaña nueva: es la única forma de cumplir lo prometido sin
  // decir que llegará un email que no se ha encolado.
  const openKit = () => {
    if (!result || !result.contentHtml) return;
    const url = URL.createObjectURL(new Blob([result.contentHtml], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const termsLink = t('aikit_form_terms_link');
  const termsParts = t('aikit_form_terms').split(termsLink);
  const termsBefore = termsParts[0];
  const termsAfter = termsParts.length > 1 ? termsParts[1] : '';

  return (
    <div
      className="aik-modal"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
    >
      <div
        className="aik-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aik-modal-title"
        ref={dialogRef}
      >
        <button
          type="button"
          className="aik-modal__close"
          onClick={() => { if (!sending) onClose(); }}
          disabled={sending}
          aria-label={t('aikit_form_close')}
        >
          <FiX size={20} aria-hidden="true" />
        </button>

        {done && isNewsletter ? (
          // Alta en la newsletter: ni kit ni oferta del webinar. Una sola salida,
          // la auditoría, para quien quiere ir más rápido que la newsletter.
          <div className="aik-done">
            <span className="aik-done__check" aria-hidden="true"><FiCheck size={26} /></span>
            <h2 className="aik-modal__title" id="aik-modal-title" role="status">
              {/* Por la vía de reserva solo ha llegado a un buzón: no se dice
                  que ya está dentro. */}
              {result && result.fallback ? t('newsletter_popup_ok_fallback') : t('newsletter_popup_ok')}
            </h2>
            {result && result.fallback ? null : (
              <p className="aik-done__text">{t('newsletter_popup_ok_text')}</p>
            )}
            <p className="aik-done__next">{t('newsletter_popup_ok_next')}</p>
            <div className="aik-done__actions">
              <a
                className="aik-btn aik-btn--solid"
                href={businessLogic.aiAuditLink}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackConversion('Newsletter Audit From Thanks')}
              >
                {t('aikit_done_audit')} <FiArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
          </div>
        ) : done ? (
          // PANTALLA de gracias, no un aviso: el visitante acaba de hacer lo que
          // le pedíamos, así que este es el mejor momento para enseñarle el
          // siguiente paso. Dos salidas y ninguna más: la oferta (que caduca) y
          // la auditoría (para quien no compra hoy).
          <div className="aik-done">
            <span className="aik-done__check" aria-hidden="true"><FiCheck size={26} /></span>
            <h2 className="aik-modal__title" id="aik-modal-title" role="status">
              {result && result.emailQueued ? t(copy.ok) : t(copy.okNoEmail)}
            </h2>
            <p className="aik-done__text">
              {result && result.emailQueued ? t(copy.doneText) : t(copy.doneTextNoEmail)}
            </p>

            {result && !result.emailQueued && result.contentHtml ? (
              <button type="button" className="aik-btn aik-btn--ghost aik-done__open" onClick={openKit}>
                {t(copy.okOpen)}
              </button>
            ) : null}

            {offerLive ? <p className="aik-done__next">{t('aikit_done_next')}</p> : null}

            <div className="aik-done__actions">
              {/* Cierra el panel y lleva a la oferta. No inicia ningún pago. Solo
                  mientras la promoción está viva: después no hay a dónde ir. */}
              {offerLive ? (
                <button
                  type="button"
                  className="aik-btn aik-btn--solid"
                  onClick={() => { onClose(); onSeeOffer(); }}
                >
                  {t('aikit_form_ok_offer')} <FiArrowRight size={17} aria-hidden="true" />
                </button>
              ) : null}
              <a
                className="aik-btn aik-btn--ghost"
                href={businessLogic.aiAuditLink}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackConversion('AI Kit Audit From Thanks')}
              >
                {t('aikit_done_audit')}
              </a>
            </div>
          </div>
        ) : (
          <>
            <h2 className="aik-modal__title" id="aik-modal-title">{t(copy.title)}</h2>
            <p className="aik-modal__desc">{t(copy.desc)}</p>

            <form className="aik-form" onSubmit={handleSubmit} noValidate>
              <div className="aik-field">
                <label className="aik-field__label" htmlFor="aik-email">
                  {t('aikit_form_email')}
                </label>
                <input
                  id="aik-email"
                  ref={emailRef}
                  className={`aik-field__input${fieldError ? ' is-invalid' : ''}`}
                  type="email"
                  name="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={t('aikit_form_email_ph')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={fieldError ? 'true' : 'false'}
                  aria-describedby={fieldError ? 'aik-email-error' : undefined}
                  required
                />
                {fieldError ? (
                  <p className="aik-field__error" id="aik-email-error" role="alert">{fieldError}</p>
                ) : null}
              </div>

              {/* Obligatoria. Va primero porque es la condición para enviar. */}
              <label className="aik-check">
                <input
                  type="checkbox"
                  ref={termsRef}
                  checked={terms}
                  onChange={(e) => { setTerms(e.target.checked); setTermsError(''); }}
                  aria-invalid={termsError ? 'true' : 'false'}
                  aria-describedby={termsError ? 'aik-terms-error' : undefined}
                  required
                />
                <span>
                  {termsBefore}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">{termsLink}</Link>
                  {termsAfter}
                </span>
              </label>
              {termsError ? (
                <p className="aik-field__error" id="aik-terms-error" role="alert">{termsError}</p>
              ) : null}

              {/* Kit: opcional y desmarcada, el ÚNICO consentimiento comercial.
                  Newsletter: no hay casilla, suscribirse ya es eso. */}
              {isNewsletter ? null : (
                <label className="aik-check">
                  <input
                    type="checkbox"
                    checked={newsletter}
                    onChange={(e) => setNewsletter(e.target.checked)}
                  />
                  <span>{t('aikit_form_newsletter')}</span>
                </label>
              )}

              {/* Newsletter: la frase que convierte el botón en consentimiento va
                  ENCIMA del botón. Debajo, en un móvil apaisado quedaba fuera de
                  la vista del panel y se podía suscribir sin haberla visto. */}
              {isNewsletter ? (
                <p className="aik-form__consent" id="aik-consent">{t(copy.support)}</p>
              ) : null}

              {/* aria-describedby: quien navega con lector de pantalla oye la
                  frase de consentimiento al llegar al botón, no solo la ve. */}
              <button
                type="submit"
                className="aik-btn aik-btn--solid aik-form__submit"
                disabled={sending}
                aria-describedby={isNewsletter ? 'aik-consent' : undefined}
              >
                {sending ? t('aikit_form_sending') : t(copy.submit)}
              </button>

              {status === 'error' ? (
                <p className="aik-form__error" role="alert">{t(copy.error)}</p>
              ) : null}

              {isNewsletter ? null : <p className="aik-form__support">{t(copy.support)}</p>}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default KitRequestModal;
